<template>
	<div class="view view-share-content">
		<banner />

		<template v-if="$store.state.save && $store.state.save.content.length > 0">
			<div class="main-content main-content--max-width">
				<div class="container">
					<div class="page-header">
						<h1 class="content-title">Almost there...</h1>
						<div class="page-header__text">
							Preview your choices for sharing the content.
						</div>
					</div>

					<div class="page-section">
						<h2 class="page-section__heading">How will recipients see the content?</h2>
						<p>
							Make sure you have previewed your content in your My Saved Info. This shows you how the recipient
							will see your content.
						</p>
					</div>

					<div class="page-section">
						<label for="share-link" class="h2 page-section__heading">Your Unique Share Link</label>
						<p>
							This is your unique link which you can share with anybody you wish, the link takes the user to
							an online version of Your Saved Information.
						</p>
						<input
							type="text"
							readonly
							id="share-link"
							name="share-link"
							:value="shareLink"
							onclick="this.select();"
						/>
						<button
							type="button"
							class="btn btn--quaternary btn--full btn--tall btn--centered"
							@click="copyShareLink"
						>
							<span v-if="!linkCopied">Copy Share Link</span>
							<strong v-else>Copied!</strong>
						</button>
					</div>

					<div class="page-section">
						<h2 class="page-section__heading">Download the Info</h2>
						<p>
							You can also download the information in PDF format, to print later or even give to someone in
							your own time.
						</p>
						<a
							:href="pdfUrl"
							download
							target="_blank"
							rel="noopener"
							class="btn btn--quaternary btn--full btn--tall btn--centered"
							:class="{ 'btn--disabled' : !pdfUrl }"
							@click="pushToDataLayer('download-pdf-click')"
						>
							<template v-if="pdfFailed">Sorry, we were unable to generate your PDF, please refresh the page to try again</template>
							<template v-else-if="pdfUrl">Download PDF</template>
							<template v-else>Generating PDF...</template>
						</a>
					</div>

					<div class="page-section">
						<h2 class="page-section__heading">Share on social</h2>
						<p>
							Share the link to your content using your favourite social media apps, or email the content to
							yourself.
						</p>

						<share-page
							title="My Looped in content"
							:customUrl="shareLink"
							:standalone="true"
						/>
					</div>
				</div>
			</div>

			<div class="footer-cta footer-cta--sticky">
				<div class="container">
					<button
						@click.prevent="confirmationOpen = true"
						type="button"
						class="btn btn--icon btn--icon-reverse btn--icon-home btn--full btn--centered"
					>
						<span>OK, I'm finished!</span>
						<svg svg-inline="" focusable="false" role="presentation" viewBox="0 0 30 49" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M18.423 6.84c-.317-.765-.454-1.648-.207-2.553.71-2.607 3.554-1.396 3.848.059.294 1.454-.493 4.818-1.29 4.898-.317.031-.901-.336-1.444-.954l-.349 3.631-2.39-.508 1.832-4.573z" fill="#B28B67"/><path d="M23.515 2.725c-.003-.226-.097-.44-.265-.532a.398.398 0 0 0-.13-.045c-.016-.002-.022.002-.026-.013-.004-.012.001-.037.002-.05 0-.038 0-.078-.004-.116a.964.964 0 0 0-.107-.363.85.85 0 0 0-.468-.426.656.656 0 0 0-.333-.014 1.183 1.183 0 0 0-.173.05c-.017.005-.076.041-.091.035-.016-.007-.04-.066-.05-.082a.842.842 0 0 0-.542-.348c-.24-.048-.472-.082-.709.055a1.478 1.478 0 0 0-.262.197c-.023.024-.022.033-.043.009a.43.43 0 0 0-.3-.156.624.624 0 0 0-.206.026c-.023.007-.08.04-.103.036-.022-.004-.061-.055-.082-.07a.635.635 0 0 0-.374-.126.91.91 0 0 0-.415.103.577.577 0 0 0-.173.128c-.022.024-.043.05-.067.073-.014.013-.04.024-.05.042.007-.011-.127-.114-.14-.121a.328.328 0 0 0-.227-.037c-.15.026-.295.127-.42.236a1.29 1.29 0 0 0-.25.294c-.012.018-.022.04-.035.056-.017.02-.025.018-.05.023a.718.718 0 0 0-.345.181.958.958 0 0 0-.237.367c-.003.008-.012.044-.017.048-.007.005-.029-.002-.038-.003a.656.656 0 0 0-.262.037c-.218.077-.402.269-.523.503a1.494 1.494 0 0 0-.158.82.904.904 0 0 0 .023.13c.007.026.01.027-.002.05-.014.025-.035.048-.051.073-.051.08-.094.167-.129.258-.056.15-.102.309-.112.47-.004.074 0 .149.015.22a.63.63 0 0 0 .055.152c.005.01.012.018.015.028.01.024.016.007.002.037-.019.042-.05.08-.072.12a2.521 2.521 0 0 0-.18.39 1.085 1.085 0 0 0-.065.55.539.539 0 0 0 .085.207c.02.028.043.055.068.077.013.012.03.02.033.039.004.02-.015.055-.022.076-.06.179-.112.37-.106.559.005.174.064.34.16.46.184.236.49.29.758.19a.84.84 0 0 0 .202-.109c.084.246.355.297.56.275.255-.027.49-.191.658-.438.102.26.434.253.591.029a.711.711 0 0 0 .09-.184c.011-.035.016-.07.027-.105.013-.044.046-.083.07-.123a.896.896 0 0 0 .1-.253c.01-.047.015-.094.021-.142.006-.043.024-.08.038-.121a.384.384 0 0 1-.187-.071.403.403 0 0 1-.121-.162c-.067-.14-.09-.31-.096-.475-.012-.363.111-.769.431-.899.15-.06.54.023.686.1.154.08.244.566.244.61 0 .027-.01.059.006.08.035.047.119-.004.157-.03a.724.724 0 0 0 .17-.18c.107-.15.203-.315.283-.487.061-.132.112-.272.183-.399a.655.655 0 0 1 .279-.288c.128-.06.264-.077.396-.104a.876.876 0 0 0 .38-.155.557.557 0 0 0 .073-.066c.018-.02.04-.06.063-.07.035-.016.068.051.09.078.11.131.189.299.244.475.054.176.094.342.116.53.016.138.008.31.062.433.024.055.272.137.363.113a.528.528 0 0 0 .248-.173.418.418 0 0 0 .07-.216c.002-.046-.004-.09-.002-.136 0-.038.002-.074-.005-.11a.23.23 0 0 0-.103-.157.787.787 0 0 0 .19-.206 1.23 1.23 0 0 0 .14-.293.459.459 0 0 0 .01-.248c-.023-.083-.08-.147-.13-.206a1.3 1.3 0 0 0 .297-.261c.172-.202.309-.474.304-.76z" fill="#191847"/><path d="M10.233 18.885c.186 3.005 12.19 11.852 12.26 12.1.03.11-1.215 3.598-3.737 10.462l1.05.482c3.867-7.27 5.702-11.292 5.504-12.067-.285-1.11-3.39-4.757-9.314-10.945l-5.763-.032z" fill="#B28B67"/><path d="M19.032 39.897l1.713.737c3.613-4.816 5.028-8.79 4.77-10.815-.172-1.35-3.265-4.992-9.28-10.927H10.11c.209 6.96 11.334 12.12 11.483 12.416.149.297-1.316 3.201-2.56 8.589z" fill="#2F3676"/><path d="M17.988 41.89c-.05.09-.088.168-.113.236-.076.208-.16.523-.251.942l5.088 1.852c.336-.512.37-.842.101-.99-.268-.15-.49-.276-.666-.38l-1.685-1.99a.32.32 0 0 0-.497.01l-.157.203c-.321.061-.564.062-.73.002a.632.632 0 0 1-.154-.087.64.64 0 0 0-.936.202z" fill="#E4E4E4"/><path fill="#997659" d="M11.613 18.885L6.266 34.054 1.01 46.877h1.24l13.566-27.992z"/><path d="M.78 46.875a1.624 1.624 0 0 0-.025.26c0 .222.03.546.087.972h5.415c.14-.597.06-.919-.244-.966-.303-.048-.555-.09-.756-.129l-2.264-1.294a.32.32 0 0 0-.463.18l-.079.244c-.28.168-.509.252-.684.252a.632.632 0 0 1-.175-.029.64.64 0 0 0-.811.51z" fill="#E4E4E4"/><path d="M1.485 44.213l2.755.61c2.13-3.7 11.045-18.874 12.023-25.938h-6.134C6.865 23.99 3.516 40.45 1.485 44.213z" fill="#5C63AB"/><g><path d="M22.659 18.178l3.977.819c.941-.216 1.76-.292 2.457-.23.179.062.43.248-.15.51-.582.261-1.186.565-1.198.82-.012.253.41.319.341.69-.045.248-.692.043-1.94-.615l-3.594.29.107-2.284zm-12.15-3.463l2.07.805c-4.65 5.315-7.08 8.1-7.289 8.354-.47.573-.39 1.751-.399 2.365-.78.101-.214-1.167-1.205-1.021-.905.133-1.762.674-2.375-.297-.075-.12-.077-.461.292-.585.918-.31 2.31-.936 2.574-1.217.36-.383 2.47-3.184 6.332-8.404z" fill="#997659"/><path d="M19.243 8.942l1.574.755c-1.28 4.528-.161 8.127 5.075 9.064l-1.514 5.883c-4.833-1.038-7.984-4.78-6.51-11.069.382-1.628.832-3.301 1.375-4.633z" fill="#AFB9C5"/><path d="M18.661 8.064l2.2 1.626c-1.965 5.116-1.87 8.898-2.623 12.926l-6.645-2.551a85.972 85.972 0 0 0-1.72 3.938l-4.976-1.91C8.81 15.273 13.343 10.575 18.5 8l.161.062.001.002z" fill="#C5CFD6"/><path d="M15.443 13.168c-1.395 3.122-2.228 5.594-2.5 7.415l-1.35-.518c1.174-2.504 2.435-4.812 3.85-6.897z" fill-opacity=".1" fill="#000"/></g></g></svg>
					</button>
				</div>
			</div>

		</template>

		<div
			v-else
			class="main-content"
		>
			<div class="container">
				<div class="page-header">
					<h1 class="content-title">Please save some content first</h1>
					<p class="page-header__text">
						You don't have any saved content. Choose your content then return to this page to get your share link and file.
					</p>

					<button
						type="button"
						@click="$store.commit('modalVisible', { modal: 'chooseTopic', boolean: true })"
						class="btn btn--quaternary btn--full btn--tall btn--centered"
					>
						View topics
					</button>
				</div>
			</div>
		</div>

		<confirmation
			v-if="confirmationOpen"
			heading="Are you sure you want to finish adding content?"
			confirm-text="I'm sure"
			:confirm-method="finished"
			close-text="Cancel"
			:close-method="confirmationClose"
		>
			This means you will no longer be able to add or remove content.
		</confirmation>
	</div>
</template>

<script>
// Mixins
import { tagManager } from '@/mixins/tag-manager.js';

// Components
import Banner from '@/components/blocks/Banner.vue';
import Confirmation from '@/components/global/Confirmation.vue';
import SharePage from '@/components/blocks/SharePage.vue';

export default {
	mixins: [tagManager],
	components: {
		Banner,
		Confirmation,
		SharePage
	},
	data() {
		return {
			confirmationOpen: false,
			pdfFailed: false,
			pdfUrl: null,
			timeout: null,
			timeoutCount: 0,
			linkCopied: false,
			copyTimeout: null
		};
	},
	computed: {
		shareLink() {
			return this.$store.state.base_url + '/shared/' + this.$store.state.save.uid;
		}
	},
	methods: {
		unsetTimeout() {
			clearTimeout(this.timeout);
			this.timeout = null;
		},
		setTimeout() {
			this.timeout = setTimeout(() => this.generatePdf(), 4000);
		},
		generatePdf() {
			this.unsetTimeout();

			if (this.pdfUrl) {
				return;
			} else if (this.timeoutCount > 10) {
				this.pdfFailed = true;
				return;
			} else {
				this.timeoutCount += 1;
			}

			this.$saves
				.getPdf(this.$store.state.save.uid)
				.then(response => {
					const data = response.getData().data;

					if (data && data.status && data.status === 'generated') {
						this.pdfUrl = data.uri;
					}

					this.setTimeout();
				})
				.catch(() => {
					// If error set timeout also
					this.setTimeout();
				});
		},
		setGeneratePdf() {
			// Resets
			this.unsetTimeout();
			this.pdfUrl = null;
			this.pdfFailed = false;
			this.timeoutCount = 0;

			this.$nextTick(() => {
				if (!this.pdfUrl) {
					this.generatePdf();
				}
			});
		},
		copyShareLink() {
			this.pushToDataLayer('copy-link-click');

			const text = document.getElementById('share-link');

			clearTimeout(this.copyTimeout);

			this.$copyText(this.shareLink).then(e => {
				text.focus();
				text.setSelectionRange(0,99999);
				this.linkCopied = true;
				this.copyTimeout = setTimeout(() => {
					this.linkCopied = false;
				}, 3000)
			}, function () {
				alert('Sorry, there was an issue copying the URL, please do this manually instead.');
			});
		},
		finished() {
			this.$store.dispatch('removeSaveAndRecipients');
			this.$router.push({
				name: this.$GLOBALS.ROUTE_FINISHED
			});
		},
		confirmationClose() {
			this.confirmationOpen = false;
		}
	},
	created() {
		if (!this.$store.state.save) {
			this.$store.watch(
				(state) => this.$store.state.save,
				(value) => {
					if (value) {
						this.generatePdf();
					}
				}
			)
		} else {
			this.generatePdf();
		}
		
		this.$eventBus.$on('generatePdf', this.setGeneratePdf);
	},
	beforeRouteLeave(to, from, next) {
		this.unsetTimeout();
		next();
	}
};
</script>
